
import { Component, Emit, namespace, Prop, Vue } from "nuxt-property-decorator";
import Icon from "~/components/atoms/embedding/Icon.vue";
import {
  returnPriceFormatDE,
  returPriceTrendFormatDE,
} from "~/mixins/formatting";
import type {
  InputValues,
  PelletsState,
  SupportMessages,
} from "~/types/Pellets";
import { STANDARD_PELLETS_QUANTITY } from "~/types/Pellets";
import { buildPelletsCheckoutQuery } from "~/utils/pelletsCheckout";
import {
  calculatePelletsAmount,
  getSorteConfig,
  PELLETS_AMOUNT_LIMITS,
} from "~/utils/pelletsSorte";
import { readPelletsPreset, writePelletsPreset } from "~/utils/pelletsStorage";
import { validateZipAndAmount } from "~/utils/priceFormValidation";
import { getTrendDirection } from "~/utils/trendanalysis";
import { isValidGermanZipCode } from "~/utils/zipCode";

import iconBigbagYellow from "~/assets/images/pellets/pellets-bigbag-yellow.svg";
import iconBigbag from "~/assets/images/pellets/pellets-bigbag.svg";
import iconLooseYellow from "~/assets/images/pellets/pellets-loose-yellow.svg";
import iconLoose from "~/assets/images/pellets/pellets-loose.svg";
import iconPalletYellow from "~/assets/images/pellets/pellets-pallet-yellow.svg";
import iconPallet from "~/assets/images/pellets/pellets-pallet.svg";

const SORTE_ICONS: Record<"loose" | "pallet" | "bigbag", string> = {
  loose: iconLoose,
  pallet: iconPallet,
  bigbag: iconBigbag,
};

const SORTE_ICONS_YELLOW: Record<"loose" | "pallet" | "bigbag", string> = {
  loose: iconLooseYellow,
  pallet: iconPalletYellow,
  bigbag: iconBigbagYellow,
};

const CalculatorModule = namespace("calculator");
const OilpriceModule = namespace("oilprice");

interface LivePriceData {
  value: number;
  diff: number;
  source: "zip" | "national";
}

interface ZipPriceData {
  loose: number | null;
  pallet: number | null;
}

type SorteType = "loose" | "pallet" | "bigbag";

@Component({
  components: { Icon },
  methods: {
    returnPriceFormatDE,
    returPriceTrendFormatDE,
    getTrendDirection,
  },
})
export default class PelletsCtaWidget extends Vue {
  @Prop({ default: STANDARD_PELLETS_QUANTITY }) defaultAmount!: number;
  @Prop({ default: 1000 }) minAmount!: number;
  @Prop({ default: 50000 }) maxAmount!: number;
  @Prop({ default: null }) priceData!: PelletsState | null;

  selectedSorte: SorteType = "loose";

  currentTime = "";
  private currentTimeIntervalId: ReturnType<typeof setInterval> | null = null;

  inputValues: InputValues = {
    zipCode: "",
    amount: this.defaultAmount,
  };

  supportMessages: SupportMessages = {
    zipCode: "",
    amount: "",
  };

  zipSpecificPrice: ZipPriceData = {
    loose: null,
    pallet: null,
  };

  private intersectionObserver: IntersectionObserver | null = null;

  sorteOptions = [
    { value: "loose", name: "Lose Ware" },
    { value: "pallet", name: "Sackware" },
    { value: "bigbag", name: "Big Bag" },
  ];

  @CalculatorModule.Action("fetchAvailableOptions")
  fetchAvailableOptions!: any;

  @OilpriceModule.State("pelletsStateAssetsPrice")
  pelletsStateAssetsPrice!: PelletsState;

  originalZipCode = "";

  mounted() {
    const preset = readPelletsPreset(this.minAmount);
    if (preset.zipCode) {
      this.originalZipCode = preset.zipCode;
      this.inputValues.zipCode = preset.zipCode;
    }
    this.currentTime = this.formatCurrentTime();
    this.currentTimeIntervalId = setInterval(() => {
      this.currentTime = this.formatCurrentTime();
    }, 60_000);
    this.setupIntersectionObserver();
  }

  beforeDestroy() {
    this.intersectionObserver?.disconnect();
    if (this.currentTimeIntervalId) {
      clearInterval(this.currentTimeIntervalId);
      this.currentTimeIntervalId = null;
    }
  }

  formatCurrentTime(): string {
    const now = new Date();
    return `${now.getHours().toString().padStart(2, "0")}:${now
      .getMinutes()
      .toString()
      .padStart(2, "0")}`;
  }

  setupIntersectionObserver() {
    if (typeof IntersectionObserver === "undefined") return;

    const ctaInputs = this.$refs.ctaInputs as HTMLElement;
    if (!ctaInputs) return;

    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        const isVisible = entries[0].isIntersecting;
        this.onVisibilityChange(isVisible);
      },
      {
        threshold: 0,
        rootMargin: `-40px 0px 0px 0px`,
      },
    );

    this.intersectionObserver.observe(ctaInputs);
  }

  @Emit("visibility-change")
  onVisibilityChange(visible: boolean): boolean {
    return visible;
  }

  isValidZipCode(zipCode: string): boolean {
    return isValidGermanZipCode(zipCode);
  }

  get activePriceSource(): PelletsState {
    return this.priceData || this.pelletsStateAssetsPrice;
  }

  get livePrice(): LivePriceData | null {
    if (this.selectedSorte === "bigbag") return null;

    const priceKey = this.selectedSorte === "loose" ? "loose" : "pallet";

    const zipPrice = this.zipSpecificPrice[priceKey];
    if (zipPrice !== null && zipPrice > 0) {
      const stateData = this.activePriceSource?.[priceKey];
      const lastPrice = stateData?.lastPrice?.value || zipPrice;
      const diff = zipPrice - lastPrice;

      return {
        value: zipPrice,
        diff,
        source: "zip",
      };
    }

    const stateData = this.activePriceSource?.[priceKey];
    if (!stateData?.currentPrice?.value) return null;

    const currentPrice = stateData.currentPrice.value;
    const lastPrice = stateData.lastPrice?.value || currentPrice;
    const diff = currentPrice - lastPrice;

    return {
      value: currentPrice,
      diff,
      source: "national",
    };
  }

  get currentSorteLabel(): string {
    const option = this.sorteOptions.find(
      (o) => o.value === this.selectedSorte,
    );
    return option?.name || "Lose Ware";
  }

  get amountLabel(): string {
    return getSorteConfig(this.selectedSorte).label;
  }

  get placeHolderForAmount(): string {
    return this.selectedSorte === "loose" ? "3000" : "1";
  }

  get amountLimits() {
    return this.selectedSorte === "loose"
      ? PELLETS_AMOUNT_LIMITS.loose
      : PELLETS_AMOUNT_LIMITS.packed;
  }

  get sorteIcon(): string {
    return SORTE_ICONS[this.selectedSorte];
  }

  get sorteIconYellow(): string {
    return SORTE_ICONS_YELLOW[this.selectedSorte];
  }

  get unavailableSorteLabel(): string {
    if (this.selectedSorte === "bigbag") return "Bigbag";
    return this.currentSorteLabel;
  }

  updateZipCode(value: string): void {
    this.inputValues.zipCode = value;
    this.supportMessages.zipCode = "";
    if (!this.isValidZipCode(value)) {
      this.zipSpecificPrice = { loose: null, pallet: null };
    }
  }

  updateAmount(value: number): void {
    this.inputValues.amount = value;
  }

  updateSorte(value: SorteType): void {
    this.selectedSorte = value;
    this.inputValues.amount = getSorteConfig(value).defaultAmount;
    this.supportMessages.amount = "";
  }

  validateInputs(): boolean {
    const limit = this.amountLimits;
    const isValid = validateZipAndAmount(
      this.inputValues,
      this.supportMessages,
      {
        minAmount: limit.min,
        maxAmount: limit.max,
        amountLabel: limit.unit,
      },
    );

    if (
      this.selectedSorte !== "loose" &&
      !Number.isInteger(this.inputValues.amount)
    ) {
      this.supportMessages.amount = `Bitte nur ganze ${
        getSorteConfig(this.selectedSorte).labelShort
      } angeben`;
      return false;
    }

    return isValid;
  }

  async redirectToComparsionPage() {
    if (!this.validateInputs()) {
      return;
    }

    const amountInKg = calculatePelletsAmount(
      Number(this.inputValues.amount),
      this.selectedSorte,
    );

    try {
      await this.fetchAvailableOptions({
        isPellets: true,
        zipcode: this.inputValues.zipCode,
        amount: amountInKg,
      });

      writePelletsPreset(this.inputValues.zipCode, Number(amountInKg));

      this.$router.push({
        path: "/pelletspreise/bestellung",
        query: buildPelletsCheckoutQuery(
          this.inputValues.zipCode,
          amountInKg,
          this.selectedSorte,
        ),
      });
    } catch (error) {
      this.supportMessages.zipCode = "Ungültige Postleitzahl";
    }
  }
}
